import { useState, useCallback, useMemo } from 'react'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import type { OtherDocumentsStepContext } from '@/features/bookkeeping/end-of-year-review/types'
import type { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import './styles.scss'

export interface OtherDocumentsProps {
  stepContext: OtherDocumentsStepContext | undefined
  setStepContext: (context: OtherDocumentsStepContext) => void
  userDocuments: UserDocument[]
  isFollowUp: boolean
  taxYear: number
  /**
   * Additional document IDs to display from other contexts (payroll, contractor, assets).
   * These are "Other Documents" uploaded on other screens that should appear in the summary.
   */
  additionalOtherDocumentIds?: number[]
  stepStatus?: string
}

/**
 * OtherDocuments
 *
 * Reusable component for managing miscellaneous business document uploads.
 * This is a provider-less document type with flexible upload requirements.
 *
 * Features:
 * - Flexible document uploads for uncategorized business documents
 * - Simple documentIds array structure (no complex document metadata)
 * - Embedded FileUploadModal for uploads
 * - Hidden in follow-up mode (only shown during initial document submission)
 * - NO "not relevant" workflow (documents are optional by nature)
 * - Uses existing V1 UI components (DocumentUploadCard, FileUploadModal)
 *
 * Data Structure Note:
 * Unlike other document types, "Other Documents" uses a simple documentIds array
 * instead of a documents array with metadata. This is because these documents
 * don't have specific categories or requirements - they're just optional uploads.
 *
 * Provider-less Pattern:
 * - No provider parameter needed
 * - Does NOT include provider field in document objects
 * - Simple upload workflow without category-specific logic
 *
 * @example
 * ```tsx
 * <OtherDocuments
 *   stepContext={otherDocsContext}
 *   setStepContext={setOtherDocsContext}
 *   userDocuments={allUserDocuments}
 *   isFollowUp={false}
 *   taxYear={2024}
 * />
 * ```
 */
export function OtherDocuments({
  stepContext,
  setStepContext,
  userDocuments,
  isFollowUp,
  taxYear,
  additionalOtherDocumentIds = [],
}: OtherDocumentsProps) {
  const [uploadDialog, setUploadDialog] = useState(false)

  const otherDocs = useMemo(() => {
    // Combine document IDs from this context with additional IDs from other contexts
    const allOtherDocumentIds = [
      ...(stepContext?.documentIds ?? []),
      ...additionalOtherDocumentIds,
    ]
    return userDocuments.filter((doc) => allOtherDocumentIds.includes(doc.id))
  }, [userDocuments, stepContext?.documentIds, additionalOtherDocumentIds])

  const status = useMemo(
    () => (otherDocs.length > 0 ? 'success' : 'none'),
    [otherDocs.length]
  )

  const handleUploadClick = useCallback(() => {
    setUploadDialog(true)
  }, [])

  const handleFileUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      const newDocumentIds = [
        ...(stepContext?.documentIds ?? []),
        uploadedFile.id,
      ]

      setStepContext({
        ...stepContext,
        documentIds: newDocumentIds,
      })

      setUploadDialog(false)
    },
    [stepContext, setStepContext]
  )

  // Handle document deletion - remove from step context documentIds array
  const handleDocumentDeleted = useCallback(
    (deletedDocumentId: number) => {
      setStepContext({
        ...stepContext,
        documentIds:
          stepContext?.documentIds?.filter((id) => id !== deletedDocumentId) ??
          [],
      })
    },
    [stepContext, setStepContext]
  )

  if (isFollowUp) {
    return null
  }

  return (
    <>
      <div className="other-documents">
        <h2>Other</h2>

        <div className="other-documents__card-container">
          <DocumentUploadCard
            name="Other documents"
            description="Upload any other documents you'd like to share with the bookkeeping team here."
            status={status}
            documents={otherDocs}
            handleUploadBtn={handleUploadClick}
            isFollowUp={isFollowUp}
            onDocumentDeleted={handleDocumentDeleted}
            hideValidationError
          />
        </div>
      </div>

      <FileUploadModal
        open={uploadDialog}
        close={() => setUploadDialog(false)}
        documentType={UploadDocumentType.OTHER}
        categoryInternalName="other"
        userFacing
        year={taxYear.toString()}
        setUploadedFile={handleFileUploaded}
      />
    </>
  )
}
